import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import { shouldHideQuantityForBulkReturns } from "@redotech/redo-model/draft-return/bulk-returns";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { Adjustment } from "@redotech/redo-model/return-flow";
import { useMemo } from "react";
import { CustomerPortalAppSettings } from "../../settings";
import {
  ReturnItemParams,
  subtitleWithoutDefault,
  variantsWithoutDefault,
} from "../../util";

interface UseItemDetailResponse {
  formattedItemValue: string;
  formattedPrice?: string;
  texts: {
    title: string;
    subtitle?: string;
    textStrings: string[];
    discountExplanation?: string;
    badgeText?: string;
    bonusText?: string;
    merchantCreatedPreRejectMessage?: string;
  };
  loading: boolean;
  imageUri: string;
  bonusBadge?: { text: string; color: PillTheme };
  returnTypeBadges?: { text: string; color: PillTheme }[];
  isTnplContractLineItem?: boolean;
}

export function useItemDetail({
  item,
  isVertical = false,
  bonus,
  hideQuantity = false,
  settings,
}: {
  item?: ReturnItemParams;
  isVertical?: boolean;
  bonus?: { value: number; currency: Currency };
  hideQuantity?: boolean;
  settings: CustomerPortalAppSettings | undefined;
}): UseItemDetailResponse {
  const image = item?.imageSrc;

  const shouldHideQuantity = useMemo(() => {
    if (hideQuantity) {
      return true;
    }
    if (!item || !settings) {
      return false;
    }

    return shouldHideQuantityForBulkReturns(
      item.collections,
      settings?.bulkReturnsEnabled,
      settings?.bulkReturns?.collections,
      settings?.bulkReturns?.hideQuantity,
    );
  }, [settings, hideQuantity, item]);

  // Different than shouldHideQuantity because that also includes hideQuantity prop
  const shouldCombinePrice = useMemo(() => {
    if (!item || !settings) {
      return false;
    }

    return shouldHideQuantityForBulkReturns(
      item.collections,
      settings?.bulkReturnsEnabled,
      settings?.bulkReturns?.collections,
      settings?.bulkReturns?.hideQuantity,
    );
  }, [settings, item]);

  const { formattedPrice, formattedItemValue, discountExplanation } =
    getItemDetailPrice(item, shouldCombinePrice);

  if (!item) {
    return {
      imageUri: "",
      loading: true,
      formattedItemValue: "0",
      formattedPrice: "0",
      texts: { title: "", textStrings: [] },
    };
  }

  const { quantity, variants } = item;
  const retainedVariants = variantsWithoutDefault(variants);
  const isTnplContractLineItem = !!item.isTnplContractLineItem;

  /** In horizontal mode, display quantity, price, and variants all in one line. In vertical mode, display each variant on a new line. */
  const horizontalQuantityMessage =
    !shouldHideQuantity && quantity > 1 ? `Qty: ${quantity} / ` : "";
  const horizontalTextString = `${horizontalQuantityMessage}${formattedItemValue}${retainedVariants.length > 0 ? " / " : ""}${retainedVariants.map((v) => v.value).join(" / ")}`;
  const textStrings = [];
  if (isVertical) {
    textStrings.push(...retainedVariants.map((v) => `${v.name}: ${v.value}`));
    if (!shouldHideQuantity && quantity > 1) {
      textStrings.push(`Quantity: ${quantity}`);
    }
  } else {
    textStrings.push(horizontalTextString);
  }

  return {
    loading: false,
    texts: {
      title: item.title,
      subtitle: subtitleWithoutDefault(item.subtitle),
      textStrings,
      discountExplanation,
      badgeText: item.badgeText,
      merchantCreatedPreRejectMessage:
        "This item would have been rejected by your flow.",
    },
    formattedItemValue,
    formattedPrice,
    imageUri: image ?? "",
    bonusBadge: bonus
      ? {
          text: `+ ${getCurrencyFormatter(bonus.currency).format(bonus.value)} ${bonus.value > 0 ? "bonus" : "fee"}`,
          color: bonus.value > 0 ? PillTheme.SUCCESS : PillTheme.DANGER,
        }
      : undefined,
    returnTypeBadges: item.returnTypeBadges,
    isTnplContractLineItem,
  };
}

export function getItemDetailPrice(
  item:
    | {
        exchangeRate?: number;
        presentmentCurrency?: string;
        itemValue?: string | null;
        price: string;
        quantity: number;
        currency: Currency;
      }
    | undefined,
  shouldCombinePrice: boolean,
  adjustment?: Adjustment,
) {
  if (!item) {
    return { formattedItemValue: "", itemValueNumber: 0 };
  }

  const {
    exchangeRate,
    presentmentCurrency,
    itemValue,
    price,
    quantity,
    currency,
  } = item;

  function getItemValue(price_: number, quantity: number) {
    let priceToFormat = price_;
    if (shouldCombinePrice) {
      priceToFormat = price_ * quantity;
    }
    if (adjustment) {
      priceToFormat = priceToFormat + adjustment.flat;
    }
    if (exchangeRate && exchangeRate > 0) {
      priceToFormat = priceToFormat * exchangeRate;
    }
    return priceToFormat;
  }

  function formatPrice(price_: number, currency: Currency) {
    let targetCurrency = currency;
    if (exchangeRate && exchangeRate > 0 && presentmentCurrency) {
      targetCurrency = presentmentCurrency as Currency;
    }
    return getCurrencyFormatter(targetCurrency).format(price_);
  }

  const itemValueNumber = getItemValue(Number(itemValue), quantity);
  const formattedItemValue = formatPrice(itemValueNumber, currency);

  let formattedPrice;

  /** Display message for discount if applicable */
  let discountExplanation = undefined;
  if (Number(price) !== Number(itemValue)) {
    const discountFormatted = getCurrencyFormatter(currency).format(
      Number(price) - Number(itemValue),
    );
    discountExplanation = `Your discount of ${discountFormatted} has been reapplied.`;
    formattedPrice = formatPrice(
      getItemValue(Number(price), quantity),
      currency,
    );
  }
  return {
    formattedPrice,
    formattedItemValue,
    itemValueNumber,
    discountExplanation,
  };
}
